import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { formatZipListInternalPath } from '../../../../feature-vault/src/lib/repository-admin/repository-assignment-path.util';
import { ServerContextService } from '../../../../feature-vault/src/lib/servers/server-context.service';
import {
  RepositoryConfigResponse,
  RepositoryDownloadMultipleRequest,
  RepositoryListResponse,
  RepositorySearchOptions,
} from '../../../../domain/src/lib/repository/repository.model';
import {
  resolveVaultApiTenantId,
  tenantHeaders,
  withTenantParam,
} from '../shared/tenant-context.util';

/** Filtro UI "Texto" → param canónico del API (`txt`; back también acepta `text`). */
function resolveRepositorySearchFileType(fileType: string): string {
  return fileType === 'text' ? 'txt' : fileType;
}

@Injectable({ providedIn: 'root' })
export class RepositoryService {
  private readonly http = inject(HttpClient);
  private readonly serverContext = inject(ServerContextService);
  private readonly repositoryPath = '/api/repository';

  private repositoryUrl(): string {
    return this.repositoryPath;
  }

  getConfig(): Observable<RepositoryConfigResponse> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    return this.http.get<RepositoryConfigResponse>(`${this.repositoryUrl()}/config`, {
      headers: tenantHeaders(tenantId),
    });
  }

  list(path?: string, search?: string): Observable<RepositoryListResponse> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    let params = new HttpParams();
    if (path) params = params.set('path', path);
    if (search) params = params.set('search', search);
    return this.http.get<RepositoryListResponse>(`${this.repositoryUrl()}/list`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }

  listZip(zipPath: string, internalPath?: string): Observable<RepositoryListResponse> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    let params = new HttpParams().set('zip_path', zipPath);
    const formattedInternalPath = formatZipListInternalPath(internalPath);
    if (formattedInternalPath) {
      params = params.set('internal_path', formattedInternalPath);
    }
    return this.http.get<RepositoryListResponse>(`${this.repositoryUrl()}/zip/list`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }

  search(query: string, options?: RepositorySearchOptions): Observable<RepositoryListResponse> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    let params = new HttpParams().set('query', query);
    if (options?.searchInZips !== undefined) {
      params = params.set('search_in_zips', String(options.searchInZips));
    }
    if (options?.fileType) {
      params = params.set('file_type', resolveRepositorySearchFileType(options.fileType));
    }
    if (options?.maxResults) params = params.set('max_results', options.maxResults);
    if (options?.searchPath) params = params.set('search_path', options.searchPath);
    return this.http.get<RepositoryListResponse>(`${this.repositoryUrl()}/search`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
    });
  }

  download(filePath: string): Observable<Blob> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    const params = new HttpParams().set('file_path', filePath);
    return this.http.get(`${this.repositoryUrl()}/download`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
      responseType: 'blob',
    });
  }

  downloadFolder(folderPath: string): Observable<Blob> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    const params = new HttpParams().set('folder_path', folderPath);
    return this.http.get(`${this.repositoryUrl()}/download-folder`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
      responseType: 'blob',
    });
  }

  downloadFromZip(zipPath: string, filePath: string): Observable<Blob> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    const params = new HttpParams().set('zip_path', zipPath).set('file_path', filePath);
    return this.http.get(`${this.repositoryUrl()}/zip/download`, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId, params),
      responseType: 'blob',
    });
  }

  downloadMultiple(filePaths: string[]): Observable<Blob> {
    const tenantId = resolveVaultApiTenantId(this.serverContext);
    const body: RepositoryDownloadMultipleRequest = { file_paths: filePaths };
    return this.http.post(`${this.repositoryUrl()}/download-multiple`, body, {
      headers: tenantHeaders(tenantId),
      params: withTenantParam(tenantId),
      responseType: 'blob',
    });
  }
}
